// Admin — Users list (Wave E).
//
// Mirrors the legacy `tricivo_system/operations/edit_user.php` and the
// implicit list behind it. Admin-only; staff cannot see this surface.
//
// Filters live in the URL: ?q, ?role, ?status, ?page.

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, Pencil, Plus, Trash2, Users as UsersIcon } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { FilterBar } from '../../../components/ui/FilterBar/FilterBar';
import { Table, type Column, type RowAction } from '../../../components/ui/Table/Table';
import { Pagination } from '../../../components/ui/Pagination/Pagination';
import { Badge } from '../../../components/ui/Badge/Badge';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { Select } from '../../../components/form/Select';
import { useDeleteStaff, useStaffList } from './hooks/useUsers';
import type { StaffSummary } from '../../../types/api';
import styles from './UsersListPage.module.css';

const ROLE_LABELS: Record<string, string> = {
  staff: 'Staff',
  manager: 'Manager',
  admin: 'Admin',
};

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function UsersListPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const q = params.get('q') ?? '';
  const role = params.get('role') ?? '';
  const status = (params.get('status') ?? 'all') as 'active' | 'deactivated' | 'all';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  const list = useStaffList({
    page,
    status,
    role: role ? (role as 'staff' | 'manager' | 'admin') : undefined,
    q: q || undefined,
  });
  const deleteMutation = useDeleteStaff();

  const [deleteTarget, setDeleteTarget] = useState<StaffSummary | null>(null);

  const staff = list.data?.staff ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const columns: Column<StaffSummary>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        render: (_v, row) => (
          <button
            type="button"
            className={styles.refLink}
            onClick={() => navigate(`/admin/users/${encodeURIComponent(row.uuid)}`)}
          >
            {row.name}
          </button>
        ),
      },
      { key: 'username', header: 'Username' },
      { key: 'email', header: 'Email' },
      {
        key: 'role',
        header: 'Role',
        render: (_v, row) => (
          <Badge variant={row.role === 'admin' ? 'info' : 'default'}>
            {ROLE_LABELS[row.role] ?? row.role}
          </Badge>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (_v, row) =>
          row.status === 'active' ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="muted">Deactivated</Badge>
          ),
      },
      {
        key: 'last_login_at',
        header: 'Last Login',
        render: (_v, row) => formatDate(row.last_login_at),
      },
    ],
    [navigate],
  );

  const rowActions = (row: StaffSummary): RowAction<StaffSummary>[] => [
    {
      label: 'View',
      icon: Eye,
      onClick: () => navigate(`/admin/users/${encodeURIComponent(row.uuid)}`),
      variant: 'ghost',
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/admin/users/${encodeURIComponent(row.uuid)}/edit`),
      variant: 'ghost',
    },
    {
      label: 'Deactivate',
      icon: Trash2,
      onClick: () => setDeleteTarget(row),
      variant: 'danger',
      hidden: (r) => r.status === 'deactivated',
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="User Management"
        subtitle={`${total} user${total === 1 ? '' : 's'}`}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/admin/users/invite')}>
            Invite User
          </Button>
        }
      />

      <Card flush>
        <FilterBar
          search={{
            value: q,
            onChange: (v) => setParam('q', v),
            placeholder: 'Search by name, email, or username…',
            ariaLabel: 'Search users',
          }}
        >
          <Select
            aria-label="Role filter"
            value={role}
            onChange={(e) => setParam('role', e.target.value)}
            options={[
              { value: '', label: 'All roles' },
              { value: 'staff', label: 'Staff' },
              { value: 'manager', label: 'Manager' },
              { value: 'admin', label: 'Admin' },
            ]}
          />
          <Select
            aria-label="Status filter"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'deactivated', label: 'Deactivated' },
            ]}
          />
        </FilterBar>

        <Table
          columns={columns}
          data={staff as StaffSummary[]}
          keyField={(row) => row.uuid}
          loading={list.isLoading}
          emptyState={
            <div className={styles.empty}>
              <UsersIcon size={28} aria-hidden />
              <h3>No users found</h3>
              <p>Adjust your filters or invite a new user.</p>
            </div>
          }
          rowActions={rowActions}
          ariaLabel="Users"
        />

        {totalPages > 1 ? (
          <div className={styles.paginationWrap}>
            <Pagination
              pagination={{ page, limit: pagination?.limit ?? 50, total, totalPages }}
              onPageChange={(next) => setParam('page', String(next))}
            />
          </div>
        ) : null}
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Deactivate user?"
        message={
          deleteTarget !== null
            ? `Deactivate ${deleteTarget.name}? They will lose access immediately. Their record is preserved and can be restored later.`
            : ''
        }
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget === null) return;
          deleteMutation.mutate(deleteTarget.uuid, {
            onSuccess: () => {
              setDeleteTarget(null);
              void list.refetch();
            },
          });
        }}
      />
    </div>
  );
}
